/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Live `matchMedia` boolean. SSR-safe: starts `false` and only flips in an
 * effect, so prerendered HTML and the first client render agree — callers must
 * treat the first paint as "unknown, assume narrow" (or, like the floating CTA,
 * render nothing until a later signal anyway).
 */

import { useEffect, useState } from 'react';

export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia(query);
    setMatches(mq.matches);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [query]);

  return matches;
};

/**
 * Tailwind's `lg` breakpoint (64rem). The site header switches from the
 * hamburger drawer to the desktop nav row here, so everything that means
 * "desktop" in the sense of that header should read the same query.
 */
export const DESKTOP_QUERY = '(min-width: 64rem)';
